import { clientApi, logout } from "@/utils/api";
import { setMatchAuthCookies } from "@/utils/auth-cookies";
import { setToken } from "@/utils/token";

import { TLoginReqDto, TLoginResDto } from "@/api/services/main/auth/dtos/login.dto";
import { TRequestResetPasswordReqDto, TRequestResetPasswordResDto } from "@/api/services/main/auth/dtos/request-reset-password.dto";
import { TGetMeResDto } from "@/api/services/main/auth/dtos/get-me.dto";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { TRefreshTokenReqDto, TRefreshTokenResDto } from "@/api/services/main/auth/dtos/refresh-token.dto";
import { TResetPasswordReqDto, TResetPasswordResDto } from "@/api/services/main/auth/dtos/reset-password.dto";
import { TSendEmailToChangeUserEmailReqDto, TSendEmailToChangeUserEmailResDto } from "@/api/services/main/auth/dtos/send-email-to-change-email.dto";
import { TUpdateUserEmailReqDto, TUpdateUserEmailResDto } from "@/api/services/main/auth/dtos/update-user-email.dto";
import { TSetPasswordReqDto, TSetPasswordResDto } from "@/api/services/main/auth/dtos/set-password.dto";
import { TLogoutReqDto, TLogoutResDto } from "@/api/services/main/auth/dtos/logout.dto";
import { TValidateNewEmailReqDto, TValidateNewEmailResDto } from "@/api/services/main/auth/dtos/validate-new-email-exists.dto";

export const loginApi = async (payload: TLoginReqDto): Promise<TLoginResDto> => {
  const rs = await clientApi.post<TLoginResDto>("/login", payload);

  const { data } = rs.data;
  logout();
  setMatchAuthCookies(data);
  return rs.data;
};

export const verifyOAuthTokenApi = async (payload: { token: string }): Promise<TLoginResDto> => {
  const rs = await clientApi.post<TLoginResDto>("/oauth/verify-token", payload);
  return rs.data;
};

export const requestResetPasswordApi = async (payload: TRequestResetPasswordReqDto): Promise<TRequestResetPasswordResDto> => {
  const { data } = await clientApi.post<TRequestResetPasswordResDto>("/request-reset-password", payload);
  return data;
};

export const fetchMeApi = async (): Promise<TGetMeResDto> => {
  const { data } = await clientApi.get<TGetMeResDto>("/get-me");
  return data;
};

export const useMe = (options?: Omit<UseQueryOptions<TGetMeResDto>, "queryKey" | "queryFn">) => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER.FETCH_ME],
    queryFn: fetchMeApi,
    ...options,
  });
};

export const refreshTokenApi = async (payload: TRefreshTokenReqDto): Promise<TRefreshTokenResDto> => {
  const rs = await clientApi.post<TRefreshTokenResDto>("/refresh-token", payload);
  const { data } = rs.data;
  setToken(data.accessToken, data.refreshToken);
  return rs.data;
};

export const setPasswordApi = async (payload: TSetPasswordReqDto): Promise<TSetPasswordResDto> => {
  const { data } = await clientApi.post<TSetPasswordResDto>("/set-password", payload);
  return data;
};

export const resetPasswordApi = async (payload: TResetPasswordReqDto): Promise<TResetPasswordResDto> => {
  const { data } = await clientApi.post<TResetPasswordResDto>("/reset-password", payload);
  return data;
};

export const sendEmailToChangeEmailApi = async (payload: TSendEmailToChangeUserEmailReqDto): Promise<TSendEmailToChangeUserEmailResDto> => {
  const { data } = await clientApi.post<TSendEmailToChangeUserEmailResDto>("/send-email-to-change-user-email", payload);
  return data;
};

export const updateUserEmailApi = async (payload: TUpdateUserEmailReqDto): Promise<TUpdateUserEmailResDto> => {
  const { data } = await clientApi.post<TUpdateUserEmailResDto>("/update-user-email", payload);
  return data;
};

export const logoutApi = async (payload: TLogoutReqDto): Promise<TLogoutResDto> => {
  const rs = await clientApi.post<TLogoutResDto>("/logout", payload);
  return rs?.data;
};

export const validateNewEmailExistsApi = async (payload: TValidateNewEmailReqDto): Promise<TValidateNewEmailResDto> => {
  const { data } = await clientApi.post<TValidateNewEmailResDto>("/validate-new-email-exists", payload);
  return data;
};
